/**
 * Calculate durationInFrames for each scene type.
 * @param {object} scene - Scene object from JSON scenario
 * @param {number} fps - Video FPS (default 60)
 * @returns {number} durationInFrames
 */
export function calcDuration(scene, fps = 60) {
  const { type } = scene;
  const pad = Math.ceil(fps * 0.5); // 0.5s padding

  switch (type) {
    case 'intro':
      return scene.durationInFrames || fps * 3;

    case 'code-typing': {
      const totalChars = (scene.code || '').length;
      const startFrom = scene.startFrom || 0;
      const newChars = totalChars - startFrom;
      const speed = scene.typingSpeed || 38;
      return Math.ceil((newChars * speed / 1000) * fps) + pad;
    }

    case 'build': {
      const logs = (scene.buildLogs || []).length;
      const delayMs = 180;
      return Math.ceil((logs * delayMs / 1000) * fps) + pad;
    }

    case 'console': {
      const chars = (scene.output || '').length;
      const speed = scene.typingSpeed || 30;
      return Math.ceil((chars * speed / 1000) * fps) + pad;
    }

    case 'highlight':
      return scene.durationInFrames || fps * 3;

    case 'error':
      return scene.durationInFrames || fps * 4;

    case 'file-switch':
      return scene.durationInFrames || fps * 2;

    case 'menu-action':
      return scene.durationInFrames || fps * 3;

    default:
      return scene.durationInFrames || fps * 3;
  }
}

/**
 * Wrap calcDuration to account for audioDuration if present.
 * Audio duration takes priority — scene must be at least as long as its audio.
 */
export function calcDurationWithAudio(scene, fps = 60) {
  const baseDuration = calcDuration(scene, fps);
  if (scene.audioDuration) {
    const audioFrames = Math.ceil(scene.audioDuration * fps);
    return Math.max(baseDuration, audioFrames);
  }
  return baseDuration;
}

/**
 * Calculate total video duration from all scenes.
 * Uses calcDurationWithAudio to respect audioDuration.
 */
export function calcTotalDuration(scenes, fps = 60) {
  return scenes.reduce((sum, scene) => {
    const dur = scene.durationInFrames || calcDurationWithAudio(scene, fps);
    return sum + dur;
  }, 0);
}
